import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Typography } from '@mui/material';
import { FC, Fragment, JSX, useCallback, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
// components
import { Alert } from 'components/common/Alert';
import CapsuleTypeForm from './CapsuleTypeForm';
import { BackdropLoader } from 'components/common/BackdropLoader';
// Schema, styles, graphql, constants
import { capsuleTypeSchema } from 'validationSchema';
import { CapsuleTypeFormType, ParamType } from 'interfaces';
import { flexCenterBetween } from 'styles/commonComponentStyle';
import {
  CapsuleTypePayload,
  useGetCapsuleTypeLazyQuery,
  useUpdateCapsuleTypeMutation,
} from 'generated/graphql';
import {
  HTTP_STATUS,
  EDIT_CAPSULE_TYPE_TEXT,
  capsuleTypeInitialValue,
  CAPSULE_TYPE_IS_UPDATED_TEXT,
} from 'constants/index';

const Edit: FC = (): JSX.Element => {
  const navigation = useNavigate();
  const params = useParams<ParamType>();
  const { id } = params || {};

  const methods = useForm<CapsuleTypeFormType>({
    defaultValues: capsuleTypeInitialValue,
    resolver: yupResolver(capsuleTypeSchema),
  });

  const { handleSubmit, setValue } = methods;

  const [updateCapsuleType, { loading: capsuleTypeLoading }] = useUpdateCapsuleTypeMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateCapsuleType } = data;
      const { response } = updateCapsuleType || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        navigation(-1);
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const [getCapsuleType, { loading: getLoading }] = useGetCapsuleTypeLazyQuery({
    onCompleted: (data) => {
      const { getCapsuleType } = data || {};
      const { response, capsuleType } = getCapsuleType || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setValues(capsuleType as CapsuleTypePayload['capsuleType']);
      }
    },
    onError: () => {
      setValues(null);
    },
  });

  const onCapsuleTypeSubmit: SubmitHandler<CapsuleTypeFormType> = async (data) => {
    const { name, description } = data || {};

    await updateCapsuleType({
      variables: {
        updateCapsuleTypeInput: {
          id: id || '',
          name,
          description,
        },
      },
    });
  };

  const fetchCapsuleType = useCallback(async () => {
    id &&
      (await getCapsuleType({
        variables: {
          getCapsuleTypeInput: {
            id,
          },
        },
      }));
  }, [id, getCapsuleType]);

  const setValues = (params: CapsuleTypePayload['capsuleType']) => {
    const { name, description } = params || {};
    setValue('name', name || '');
    setValue('description', description || '');
  };

  useEffect(() => {
    id && fetchCapsuleType();
  }, [id, fetchCapsuleType]);

  const loading = capsuleTypeLoading || getLoading;

  return (
    <Fragment>
      <Box sx={flexCenterBetween}>
        <Typography variant="h5">{EDIT_CAPSULE_TYPE_TEXT}</Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item lg={6} md={12}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onCapsuleTypeSubmit)}>
              <CapsuleTypeForm loading={loading} isEdit />
            </form>
          </FormProvider>
        </Grid>
      </Grid>

      <BackdropLoader open={loading} text={CAPSULE_TYPE_IS_UPDATED_TEXT} />
    </Fragment>
  );
};

export default Edit;
